// Empty structures for GameState fields and the per-season report (24.11). Used by newGame,
// the report phase and test fixtures so every stream builds the same shapes.
import type { Config } from '../config';
import { RATION_RESOURCES, RESOURCES, METRICS } from './constants';
import type {
  GameState,
  HouseholdId,
  LedgerAccumulator,
  Metric,
  NormId,
  NormState,
  RationOverrides,
  RationResource,
  Resource,
  ResourceFlow,
  ResourceHistory,
  SeasonReport,
} from './types';

export function perResource<T>(make: (r: Resource) => T): Record<Resource, T> {
  const out = {} as Record<Resource, T>;
  for (const r of RESOURCES) out[r] = make(r);
  return out;
}

export function perRationResource<T>(make: (r: RationResource) => T): Record<RationResource, T> {
  const out = {} as Record<RationResource, T>;
  for (const r of RATION_RESOURCES) out[r] = make(r);
  return out;
}

export function emptyFlow(): ResourceFlow {
  return { production: 0, consumption: 0, spoilage: 0, theft: 0, other: 0, net: 0 };
}

export function emptyResourceHistory(): ResourceHistory {
  return { production: [], consumption: [], spoilage: [], theft: [], net: [] };
}

export function emptyOverrides(): RationOverrides {
  return { people: {}, households: {} };
}

export function emptySeries(): Record<Metric, number[]> {
  const out = {} as Record<Metric, number[]>;
  for (const m of METRICS) out[m] = [];
  return out;
}

export function emptyNorms(cfg: Config): Record<NormId, NormState> {
  const out = {} as Record<NormId, NormState>;
  for (const id of Object.keys(cfg.norms) as NormId[]) {
    out[id] = {
      count: 0,
      established: false,
      since: null,
      lastReinforcedTurn: null,
      announced: false,
    };
  }
  return out;
}

export function emptyLedger(): LedgerAccumulator {
  return {
    births: 0,
    deaths: [],
    peakPopulation: { value: 0, turn: 0 },
    personSeasons: {
      child: { total: 0, short: 0 },
      adult: { total: 0, short: 0 },
      elder: { total: 0, short: 0 },
    },
    wentWithout: {},
    shortageDecisions: [],
    normEvents: [],
    arrivals: [],
    departures: [],
    skillsLost: [],
    improvements: [],
    seedEaten: [],
    festivals: [],
    policyChanges: [],
    childLabourSeasons: 0,
    corporalPunishments: 0,
    executions: 0,
    exiles: 0,
    refusals: 0,
  };
}

/** A fresh report for the season about to be resolved (opened by the report phase). */
export function emptySeasonReport(state: GameState): SeasonReport {
  const population = Object.keys(state.people).length;
  return {
    turn: state.turn,
    year: state.year,
    season: state.season,
    entries: [],
    needs: {},
    demand: {},
    distribution: {},
    toolUse: {},
    outputs: {},
    services: {},
    healthLoss: {},
    crowding: {},
    stocksStart: perResource((r) => state.stocks[r].amount),
    stocksEnd: perResource((r) => state.stocks[r].amount),
    flows: perResource(() => emptyFlow()),
    population: {
      start: population,
      end: population,
      births: 0,
      deaths: 0,
      arrivals: 0,
      departures: 0,
    },
    gini: { food: 0, beds: 0 },
    fragility: 0,
    fragilityFactors: [],
    illnessMultiplier: 1,
    expectedDeaths: 0,
    steady: null,
    trustStart: householdTrust(state),
  };
}

/** Section 5 Charismatic (Stream D): mean trust per household, for the season's trust spread. */
export function householdTrust(state: GameState): Record<HouseholdId, number> {
  const out: Record<HouseholdId, number> = {};
  for (const id of Object.keys(state.households).sort() as HouseholdId[]) {
    const h = state.households[id];
    if (h === undefined) continue;
    let total = 0;
    let n = 0;
    for (const pid of h.memberIds) {
      const p = state.people[pid];
      if (p === undefined) continue;
      total += p.trust;
      n++;
    }
    if (n > 0) out[id] = total / n;
  }
  return out;
}
